import React, { useState } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  AppRegistration as RegisterIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { AppBreadcrumbs } from "../components/common";
import { useAuth } from "../contexts/AuthContext";

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    percent: 0,
    label: "Too weak",
  });

  const { signUp } = useAuth();

  const COMMON_PASSWORDS = new Set([
    "password",
    "123456",
    "123456789",
    "qwerty",
    "111111",
    "letmein",
    "welcome",
    "admin",
    "iloveyou",
    "000000",
  ]);

  const validatePassword = (
    password: string,
    context: { email: string; firstName: string; lastName: string }
  ): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    const normalized = password.toLowerCase();
    if (COMMON_PASSWORDS.has(normalized)) {
      return "Password is too common";
    }
    const emailUser = context.email.split("@")[0]?.toLowerCase();
    if (emailUser && normalized.includes(emailUser)) {
      return "Password should not include your email";
    }
    if (
      context.firstName &&
      normalized.includes(context.firstName.toLowerCase())
    ) {
      return "Password should not include your first name";
    }
    if (
      context.lastName &&
      normalized.includes(context.lastName.toLowerCase())
    ) {
      return "Password should not include your last name";
    }
    return null;
  };

  const getPasswordStrength = (password: string) => {
    if (!password) {
      return { score: 0, percent: 0, label: "Too weak" };
    }
    const length = password.length;
    const variety = [
      /[a-z]/.test(password),
      /[A-Z]/.test(password),
      /[0-9]/.test(password),
      /[^A-Za-z0-9]/.test(password),
    ].filter(Boolean).length;

    let score = 0;
    if (length >= 8) score += 1;
    if (length >= 12) score += 1;
    if (length >= 16) score += 1;
    if (variety >= 3) score += 1;

    const labels = ["Too weak", "Weak", "Fair", "Good", "Strong"];
    return {
      score,
      percent: Math.round((score / 4) * 100),
      label: labels[score],
    };
  };

  const generatePassword = (length = 16) => {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const symbols = "!@#$%^&*()-_=+[]{}<>?";
    const all = lower + upper + digits + symbols;
    const passwordChars = [
      lower[getRandomInt(lower.length)],
      upper[getRandomInt(upper.length)],
      digits[getRandomInt(digits.length)],
      symbols[getRandomInt(symbols.length)],
    ];

    for (let i = passwordChars.length; i < length; i += 1) {
      passwordChars.push(all[getRandomInt(all.length)]);
    }

    for (let i = passwordChars.length - 1; i > 0; i -= 1) {
      const j = getRandomInt(i + 1);
      [passwordChars[i], passwordChars[j]] = [
        passwordChars[j],
        passwordChars[i],
      ];
    }

    return passwordChars.join("");
  };

  const getRandomInt = (max: number) => {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    return array[0] % max;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    // Validate form
    if (!formData.agreeToTerms) {
      setError("You must agree to the terms and conditions");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const passwordError = validatePassword(formData.password, formData);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    console.debug("[DEBUG] Submitting registration", {
      email: formData.email,
      strength: passwordStrength,
    });

    try {
      const { error, requiresEmailConfirmation } = await signUp(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );

      if (error) {
        setError(error.message);
      } else if (requiresEmailConfirmation) {
        setInfo("Check your email to confirm your account before signing in.");
        console.debug("[DEBUG] Email confirmation required", {
          email: formData.email,
        });
      } else {
        console.debug("[DEBUG] Registration succeeded with session", {
          email: formData.email,
        });
      }
    } catch (err: any) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
      if (field === "password") {
        const strength = getPasswordStrength(value);
        setPasswordStrength(strength);
        console.debug("[DEBUG] Password strength update", strength);
      }
    };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      agreeToTerms: e.target.checked,
    }));
  };

  const handleGeneratePassword = () => {
    const generated = generatePassword();
    setFormData((prev) => ({
      ...prev,
      password: generated,
      confirmPassword: generated,
    }));
    const strength = getPasswordStrength(generated);
    setPasswordStrength(strength);
    setShowPassword(true);
    setShowConfirmPassword(true);
    setInfo("Generated a strong password. Save it somewhere safe.");
    console.debug("[DEBUG] Generated password", {
      length: generated.length,
      strength,
    });
  };

  return (
    <>
      <AppBreadcrumbs />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <RegisterIcon sx={{ fontSize: 40, color: "primary.main", mb: 2 }} />

            <Typography component="h1" variant="h5">
              Create Account
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                {error}
              </Alert>
            )}
            {info && (
              <Alert severity="success" sx={{ mt: 2, width: "100%" }}>
                {info}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 3, width: "100%" }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange("firstName")}
                  margin="normal"
                  autoComplete="given-name"
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange("lastName")}
                  margin="normal"
                  autoComplete="family-name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <TextField
                fullWidth
                required
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                margin="normal"
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange("password")}
                margin="normal"
                autoComplete="new-password"
                helperText="Use 8+ characters and avoid common or personal info"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Password strength: {passwordStrength.label}
                  </Typography>
                  <Button size="small" onClick={handleGeneratePassword}>
                    Generate
                  </Button>
                </Box>
                <Box sx={{ mt: 0.5 }}>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 999,
                      overflow: "hidden",
                      backgroundColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${passwordStrength.percent}%`,
                        transition: "width 200ms ease",
                        backgroundColor:
                          passwordStrength.score >= 3
                            ? "success.main"
                            : passwordStrength.score === 2
                              ? "warning.main"
                              : "error.main",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <TextField
                fullWidth
                required
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange("confirmPassword")}
                margin="normal"
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agreeToTerms}
                    onChange={handleCheckboxChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{" "}
                    <Link component={RouterLink} to="/terms" target="_blank">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link component={RouterLink} to="/privacy" target="_blank">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
                sx={{ mt: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !formData.agreeToTerms}
              >
                {loading ? "Creating Account..." : "Sign Up"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Already have an account? Sign In
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default RegisterPage;
